import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { categories, categoryGroups, FOOD_CATEGORIES } from '@/mocks/products';
import type { Category } from '@/mocks/products';

const BEAUTY_CATEGORIES: Category[] = ['섬유향수', '핸드크림', '스킨케어·뷰티'];
import { useFavorites } from '@/hooks/useFavorites';
import { useAuth } from '@/hooks/useAuth';
import LoginModal from '@/components/feature/LoginModal';

interface HeaderProps {
  onCategoryClick?: (cat: string) => void;
  forceScrolled?: boolean;
}

const navLinks = [
  { ko: '회사소개', zh: '公司介绍', href: '/about' },
  { ko: '추천상품', zh: '推荐商品', href: '/#recommended' },
  { ko: '최저가', zh: '最低价', href: '/#lowest-price' },
  { ko: '인기상품', zh: '热销商品', href: '/#popular' },
  { ko: '카테고리', zh: '分类', href: '/#categories' },
  { ko: '문의하기', zh: '询价', href: '/#inquiry' },
];
// 네비게이션: 중국어 주, 한국어 부

export default function Header({ onCategoryClick, forceScrolled = false }: HeaderProps) {
  const navigate = useNavigate();
  const { isLoggedIn, username, role, logout } = useAuth();
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [showFavTooltip, setShowFavTooltip] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { count: favCount } = useFavorites();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isScrolled = forceScrolled || scrolled;

  useEffect(() => {
    if (searchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [searchOpen]);

  const scrollToId = (id: string, delay = 0) => {
    const doScroll = () => {
      const el = document.getElementById(id);
      if (!el) return;
      const bannerHeight = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--banner-height') || '0'
      );
      // top bar: 모바일 h-14(56px) / 데스크탑 h-16(64px)
      // 카테고리 바: 한국어+중국어 2줄 약 46px (py-1 포함)
      const isMobile = window.innerWidth < 768;
      const headerHeight = isMobile ? 102 : 110;
      const offset = bannerHeight + headerHeight;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    };
    if (delay > 0) setTimeout(doScroll, delay);
    else doScroll();
  };

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    if (href.startsWith('/#')) {
      const id = href.replace('/#', '');
      if (window.location.pathname !== '/') {
        navigate('/');
        scrollToId(id, 450);
      } else {
        scrollToId(id);
      }
    } else {
      navigate(href);
    }
  };

  const handleCategoryClick = (cat: string) => {
    // 섬유향수·핸드크림은 스킨케어·뷰티 그룹 내 세부 카테고리이므로 그대로 전달
    setActiveCategory(cat);
    onCategoryClick?.(cat);
    if (window.location.pathname !== '/') {
      navigate('/');
      scrollToId('categories', 450);
    } else {
      scrollToId('categories');
    }
  };

  // 식품 그룹 클릭 시 첫 번째 식품 카테고리로 이동
  const handleFoodGroupClick = () => {
    handleCategoryClick('탕류');
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchValue.trim())}`);
      setSearchOpen(false);
      setSearchValue('');
    }
  };

  /* 스크롤 시 색 */
  const scrolledBg = '#F8F4EE';
  const scrolledBorder = '#D8CEBF';
  const scrolledText = '#2E5240';
  const scrolledSubText = '#8C7D6E';

  return (
    <>
      <header
        className={`fixed left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'border-b' : 'bg-transparent'}`}
        style={{
          top: 'var(--banner-height, 0px)',
          backgroundColor: isScrolled ? scrolledBg : undefined,
          borderColor: isScrolled ? scrolledBorder : undefined,
        }}
      >
        {/* Top bar */}
        <div className="px-3 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Logo */}
            <button onClick={() => navigate('/')} className="flex items-center cursor-pointer shrink-0">
              <div className="flex flex-col items-start leading-none">
                <span
                  className="text-[18px] md:text-[20px] font-black tracking-tight"
                  style={{ color: isScrolled ? '#2A2118' : '#fff', fontFamily: 'Impact, Arial Black, sans-serif', letterSpacing: '-0.02em' }}
                >
                  BIG BROTHER
                </span>
                <span
                  className="text-[9px] font-semibold tracking-[0.18em] uppercase"
                  style={{ color: isScrolled ? '#8C7D6E' : 'rgba(255,255,255,0.55)' }}
                >
                  Korea B2B Wholesale
                </span>
              </div>
            </button>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6">
              {navLinks.map(link => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="flex flex-col items-center transition-colors duration-200 whitespace-nowrap cursor-pointer group"
                >
                  <span
                    className={`text-[13px] font-bold tracking-wide leading-tight ${
                      isScrolled ? 'group-hover:text-[#C4985A]' : 'text-white/95 group-hover:text-white'
                    }`}
                    style={isScrolled ? { color: scrolledText } : undefined}
                  >
                    {link.zh}
                  </span>
                  <span
                    className={`text-[10px] font-normal tracking-[0.08em] leading-tight ${
                      isScrolled ? '' : 'text-white/45 group-hover:text-white/70'
                    }`}
                    style={isScrolled ? { color: scrolledSubText } : undefined}
                  >
                    {link.ko}
                  </span>
                </button>
              ))}
            </nav>

            {/* Right actions */}
            <div className="hidden md:flex items-center gap-1">
              <button
                onClick={() => setSearchOpen(true)}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                  isScrolled ? 'hover:bg-[#E5DDD0]' : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                style={isScrolled ? { color: scrolledText } : undefined}
              >
                <i className="ri-search-line text-lg"></i>
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowFavTooltip(prev => !prev)}
                  onBlur={() => setTimeout(() => setShowFavTooltip(false), 150)}
                  className={`relative w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                    isScrolled ? 'hover:bg-[#E5DDD0]' : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                  style={isScrolled ? { color: scrolledText } : undefined}
                  aria-label="관심 상품"
                >
                  {favCount > 0 ? <i className="ri-heart-fill text-lg"></i> : <i className="ri-heart-line text-lg"></i>}
                  {favCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 text-white text-[9px] font-black rounded-full flex items-center justify-center leading-none" style={{ backgroundColor: '#C4985A' }}>
                      {favCount > 99 ? '99+' : favCount}
                    </span>
                  )}
                </button>

                {showFavTooltip && (
                  <div className="absolute right-0 top-full mt-2 w-56 rounded-xl overflow-hidden z-10 animate-fadeIn border" style={{ backgroundColor: '#F8F4EE', borderColor: '#D8CEBF' }}>
                    <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: '#E5DDD0' }}>
                      <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#2A2118' }}>관심 상품</span>
                      <span className="text-xs font-semibold" style={{ color: '#8C7D6E' }}>{favCount}개</span>
                    </div>
                    <div className="px-4 py-3">
                      {favCount === 0 ? (
                        <p className="text-xs text-center py-2" style={{ color: '#8C7D6E' }}>관심 상품이 없습니다.<br />상품 카드의 ♡ 버튼을 눌러보세요.</p>
                      ) : (
                        <p className="text-xs leading-relaxed" style={{ color: '#5C4F42' }}>
                          <span className="font-bold" style={{ color: '#2A2118' }}>{favCount}개</span>의 관심 상품이 저장되어 있습니다.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {isLoggedIn ? (
                <div className="flex items-center gap-2 ml-1">
                  {role === 'admin' && (
                    <button
                      onClick={() => navigate('/admin')}
                      className="text-[12px] font-bold px-3 py-1.5 rounded-full whitespace-nowrap cursor-pointer transition-all hover:opacity-80 flex items-center gap-1"
                      style={{ backgroundColor: '#2A2118', color: '#fff' }}
                    >
                      <i className="ri-shield-user-line text-xs"></i>
                      관리자 페이지
                    </button>
                  )}
                  {role === 'partner' && (
                    <button
                      onClick={() => navigate('/partner')}
                      className="text-[12px] font-bold px-3 py-1.5 rounded-full whitespace-nowrap cursor-pointer transition-all hover:opacity-80 flex items-center gap-1"
                      style={{ backgroundColor: '#2E5240', color: '#fff' }}
                    >
                      <i className="ri-store-2-line text-xs"></i>
                      파트너 대시보드
                    </button>
                  )}
                  <span
                    className="text-[12px] font-semibold px-3 py-1.5 rounded-full whitespace-nowrap"
                    style={{ backgroundColor: '#E8F0EC', color: '#2E5240' }}
                  >
                    <i className="ri-user-line mr-1"></i>{username}
                    {role === 'admin' && <span className="ml-1 text-[10px] font-bold opacity-70">ADMIN</span>}
                    {role === 'partner' && <span className="ml-1 text-[10px] font-bold opacity-70">PARTNER</span>}
                  </span>
                  <button
                    onClick={logout}
                    className="text-[12px] font-semibold px-3 py-1.5 rounded-full whitespace-nowrap cursor-pointer transition-all hover:opacity-80"
                    style={{ backgroundColor: '#F0EAE0', color: '#5C4F42', border: '1px solid #D8CEBF' }}
                  >
                    로그아웃
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setLoginModalOpen(true)}
                  className="px-4 py-2 text-[13px] font-bold rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer ml-1 tracking-wide hover:opacity-85 flex items-center gap-1.5"
                  style={{ backgroundColor: '#F0EAE0', color: '#5C4F42', border: '1px solid #D8CEBF' }}
                >
                  <i className="ri-lock-line text-sm"></i>
                  合作伙伴登录
                </button>
              )}
              <button
                onClick={() => handleNavClick('/#inquiry')}
                className="px-5 py-2 text-white text-[13px] font-bold rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer ml-1 tracking-wide hover:opacity-85"
                style={{ backgroundColor: '#2E5240' }}
              >
                询价咨询 <span className="font-normal opacity-60 text-[11px]">/ 상담 문의</span>
              </button>
            </div>

            {/* Mobile right */}
            <div className="flex md:hidden items-center gap-1">
              <button
                onClick={() => setSearchOpen(true)}
                className={`w-10 h-10 flex items-center justify-center rounded cursor-pointer ${isScrolled ? '' : 'text-white'}`}
                style={isScrolled ? { color: scrolledText } : undefined}
              >
                <i className="ri-search-line text-xl"></i>
              </button>
              <button
                onClick={() => setShowFavTooltip(prev => !prev)}
                className={`relative w-10 h-10 flex items-center justify-center rounded cursor-pointer ${isScrolled ? '' : 'text-white'}`}
                style={isScrolled ? { color: scrolledText } : undefined}
              >
                {favCount > 0 ? <i className="ri-heart-fill text-xl"></i> : <i className="ri-heart-line text-xl"></i>}
                {favCount > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 text-white text-[9px] font-black rounded-full flex items-center justify-center" style={{ backgroundColor: '#C4985A' }}>
                    {favCount > 99 ? '99+' : favCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className={`w-10 h-10 flex items-center justify-center rounded cursor-pointer ${isScrolled ? '' : 'text-white'}`}
                style={isScrolled ? { color: scrolledText } : undefined}
              >
                <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Category nav bar */}
        <div
          className={`border-t transition-all duration-300 ${isScrolled ? '' : 'bg-black/20 backdrop-blur-sm border-white/10'}`}
          style={isScrolled ? { backgroundColor: '#F8F4EE', borderColor: '#D8CEBF' } : undefined}
        >
          <div className="overflow-x-auto scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="flex items-center gap-0.5 py-1 px-2 md:px-8 lg:px-12 w-max">

              {/* 모든 카테고리 그룹 순서대로 렌더링 */}
              {categoryGroups.map((group, idx) => {
                const hasChildren = !!group.children;
                const isActive = group.id === '스킨케어·뷰티'
                  ? BEAUTY_CATEGORIES.includes(activeCategory as Category) || activeCategory === '스킨케어·뷰티'
                  : group.id === 'food'
                  ? FOOD_CATEGORIES.includes(activeCategory as Category)
                  : activeCategory === group.id;
                const clickTarget = group.children ? group.children[0] : group.id;
                const handleClick = group.id === 'food' ? handleFoodGroupClick : () => handleCategoryClick(clickTarget);

                return (
                  <>
                    <button
                      key={group.id}
                      onClick={handleClick}
                      className="flex-shrink-0 flex flex-col items-center px-2.5 py-1 md:px-3.5 md:py-1.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
                      style={
                        isActive
                          ? { backgroundColor: '#2E5240', color: '#fff' }
                          : isScrolled
                          ? { color: '#3D6B52' }
                          : { color: 'rgba(255,255,255,0.85)' }
                      }
                    >
                      <span className="text-[11px] md:text-[12px] font-bold leading-tight flex items-center gap-0.5">
                        {group.zh}
                        {hasChildren && (
                          <i
                            className="ri-arrow-down-s-line text-[10px] leading-none"
                            style={
                              isActive
                                ? { color: 'rgba(255,255,255,0.7)' }
                                : isScrolled
                                ? { color: '#8C7D6E' }
                                : { color: 'rgba(255,255,255,0.5)' }
                            }
                          ></i>
                        )}
                      </span>
                      <span
                        className="text-[9px] font-normal leading-tight"
                        style={
                          isActive
                            ? { color: 'rgba(255,255,255,0.6)' }
                            : isScrolled
                            ? { color: '#8C7D6E' }
                            : { color: 'rgba(255,255,255,0.5)' }
                        }
                      >
                        {group.ko}
                      </span>
                    </button>
                    {/* 스킨케어·뷰티 다음에 구분선 */}
                    {idx === 0 && (
                      <span
                        key="divider"
                        className="w-px h-3.5 mx-1 shrink-0"
                        style={{ backgroundColor: isScrolled ? '#D8CEBF' : 'rgba(255,255,255,0.2)' }}
                      />
                    )}
                  </>
                );
              })}
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden border-b px-4 py-4 space-y-1" style={{ backgroundColor: '#1B2B3A', borderColor: 'rgba(255,255,255,0.1)' }}>
            {navLinks.map(link => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                className="flex items-center justify-between w-full text-left px-3 py-2.5 rounded-md cursor-pointer transition-colors"
                style={{ color: '#fff' }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.07)')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <span className="text-[14px] font-bold tracking-wide text-white">{link.zh}</span>
                <span className="text-[11px] tracking-widest" style={{ color: 'rgba(212,168,83,0.7)' }}>{link.ko}</span>
              </button>
            ))}
            {isLoggedIn ? (
              <div className="mt-2 space-y-1">
                {role === 'admin' && (
                  <button
                    onClick={() => { setMenuOpen(false); navigate('/admin'); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-bold cursor-pointer"
                    style={{ backgroundColor: '#2A2118', color: '#fff' }}
                  >
                    <i className="ri-shield-user-line"></i>
                    관리자 페이지 바로가기
                  </button>
                )}
                {role === 'partner' && (
                  <button
                    onClick={() => { setMenuOpen(false); navigate('/partner'); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-bold cursor-pointer"
                    style={{ backgroundColor: '#2E5240', color: '#fff' }}
                  >
                    <i className="ri-store-2-line"></i>
                    파트너 대시보드 바로가기
                  </button>
                )}
                <div className="flex items-center justify-between px-3 py-2.5 rounded-md" style={{ backgroundColor: 'rgba(255,255,255,0.07)' }}>
                  <span className="text-sm font-semibold text-white">
                    <i className="ri-user-line mr-1.5"></i>{username}
                    {role === 'admin' && <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: '#C4985A', color: '#fff' }}>ADMIN</span>}
                    {role === 'partner' && <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: '#2E5240', color: '#fff' }}>PARTNER</span>}
                  </span>
                  <button
                    onClick={() => { logout(); setMenuOpen(false); }}
                    className="text-xs font-semibold px-3 py-1 rounded-full cursor-pointer"
                    style={{ backgroundColor: '#F0EAE0', color: '#5C4F42' }}
                  >
                    로그아웃
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => { setMenuOpen(false); setLoginModalOpen(true); }}
                className="w-full mt-2 px-3 py-2.5 text-sm font-bold rounded-md whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                <i className="ri-lock-line"></i>
                거래처 로그인 / 合作伙伴登录
              </button>
            )}
            <button
              onClick={() => { setMenuOpen(false); handleNavClick('/#inquiry'); }}
              className="w-full mt-2 px-3 py-2.5 text-sm font-bold rounded-md whitespace-nowrap cursor-pointer"
              style={{ backgroundColor: '#D4A853', color: '#162233' }}
            >
              询价咨询 / 상담 문의
            </button>
          </div>
        )}

        {/* Mobile favorites tooltip */}
        {showFavTooltip && (
          <div className="md:hidden border-b px-4 py-3" style={{ backgroundColor: '#1B2B3A', borderColor: 'rgba(255,255,255,0.1)' }}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-white">관심 상품 {favCount}개</span>
              <button onClick={() => setShowFavTooltip(false)} className="cursor-pointer" style={{ color: 'rgba(255,255,255,0.5)' }}>
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            {favCount === 0 && (
              <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.45)' }}>상품 카드의 ♡ 버튼을 눌러 관심 상품을 저장하세요.</p>
            )}
          </div>
        )}
      </header>

      <LoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} />

      {/* Search Overlay */}
      {searchOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center pt-24 px-4"
          style={{ backgroundColor: 'rgba(42,33,24,0.55)', backdropFilter: 'blur(4px)' }}
          onClick={e => { if (e.target === e.currentTarget) setSearchOpen(false); }}
        >
          <div className="w-full max-w-2xl rounded-2xl overflow-hidden animate-fadeIn border" style={{ backgroundColor: '#F8F4EE', borderColor: '#D8CEBF' }}>
            <form onSubmit={handleSearch} className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: '#E5DDD0' }}>
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className="ri-search-line text-lg" style={{ color: '#8C7D6E' }}></i>
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
                placeholder="상품명으로 검색 / 按商品名搜索 (예: 삼계탕, 갈비탕…)"
                className="flex-1 text-base focus:outline-none bg-transparent"
                style={{ color: '#2A2118' }}
              />
              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full transition-colors cursor-pointer"
                style={{ color: '#8C7D6E' }}
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </form>

            {/* Quick keywords */}
            <div className="px-5 py-4">
              <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#8C7D6E' }}>추천 검색어 / 推荐搜索词</p>
              <div className="flex flex-wrap gap-2">
                {['삼계탕', '왕갈비탕', '화장품', '건강식품', '생활용품', 'K-뷰티', '프리미엄'].map(kw => (
                  <button
                    key={kw}
                    onClick={() => { navigate(`/search?q=${encodeURIComponent(kw)}`); setSearchOpen(false); setSearchValue(''); }}
                    className="text-xs px-3 py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap font-medium"
                    style={{ backgroundColor: '#E5DDD0', color: '#5C4F42' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#2E5240'; (e.currentTarget as HTMLButtonElement).style.color = '#fff'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#E5DDD0'; (e.currentTarget as HTMLButtonElement).style.color = '#5C4F42'; }}
                  >
                    {kw}
                  </button>
                ))}
              </div>
            </div>

            {/* Category shortcuts */}
            <div className="px-5 pb-4 pt-0 border-t" style={{ borderColor: '#E5DDD0' }}>
              <p className="text-xs font-bold uppercase tracking-wider mb-3 pt-4" style={{ color: '#8C7D6E' }}>分类 / 카테고리</p>
              <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                {categoryGroups.map(group => (
                  <button
                    key={group.id}
                    onClick={() => { navigate(`/search?q=${encodeURIComponent(group.children ? group.children[0] : group.id)}`); setSearchOpen(false); }}
                    className="py-2 text-xs font-semibold border rounded-lg transition-all cursor-pointer text-center"
                    style={{ borderColor: '#D8CEBF', color: '#5C4F42', backgroundColor: 'transparent' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#2E5240'; (e.currentTarget as HTMLButtonElement).style.color = '#fff'; (e.currentTarget as HTMLButtonElement).style.borderColor = '#2E5240'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = '#5C4F42'; (e.currentTarget as HTMLButtonElement).style.borderColor = '#D8CEBF'; }}
                  >
                    <span className="block font-bold">{group.zh}</span>
                    <span className="block text-[10px] opacity-60 mt-0.5">{group.ko}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
